import * as d3 from 'd3';
import {DebouncedFunction, debounce} from 'ts-debounce';
import _ from 'underscore';

import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {Sequence} from 'components/Sequence';
import * as vo from 'components/VisibilityOptions';
import {SliceMapType, SliceComponentName} from 'constants/MapsConstants';
import CutSite from 'molbio/enzymes/backbone/CutSite';
import Component, {ComponentOptions} from 'molbio/maps/linear/Component';
import {CutSites as CutSitesLayout, LinearLayout} from 'molbio/maps/linear/LinearLayout';
import Slice from 'molbio/maps/linear/Slice';
import {SliceView} from 'molbio/maps/linear/SliceView';
import {PolymerLinearMapState} from 'molbio/maps/MapStates';
import {TabState} from 'Navigation';

export interface CutSitesViewOptions extends ComponentOptions {
  mapType: SliceMapType;
  parent: SliceView;
  slice: Slice;
}

class CutSitesView extends Component {
  layout!: CutSitesLayout;
  _layout!: LinearLayout;
  mapType!: SliceMapType;
  tabstate!: TabState;
  opacityFactor!: number;
  fontOpacityFactor!: number;
  enableMousedown!: boolean;
  model!: PolymerLinearMapState<Sequence>;
  debouncedUpdate!: DebouncedFunction<() => void>;
  slice!: Slice;
  parent!: SliceView;
  max!: number;
  className!: SliceComponentName;
  y!: d3.ScaleLinear<number, number>;

  static initClass(): void {
    this.prototype.className = SliceComponentName.cutSites;
    this.prototype.setting = vo.CUTSITES.name;
  }

  initialize(options: CutSitesViewOptions): void {
    super.initialize(options);
    this.tabstate = options.tabstate;
    this.mapType = options.mapType;
    this.slice = options.slice;
    this.layout = this.model.get('layout').CutSites;
    this._layout = this.model.get('layout');
    this.debouncedUpdate = debounce(this.update.bind(this), 1);
    this.parent = options.parent;
  }

  die(): void {
    super.die();
    d3.select(this.el).selectAll('.cutsite').selectAll('text').on('.cutsite', null);
  }

  overhangsOption(): vo.VisibilityOption {
    return this.model.get('visibilityOptions').getOption(vo.OVERHANGS.name)!;
  }

  primersOption(): vo.VisibilityOption {
    return this.model.get('visibilityOptions').getOption(vo.PRIMERS.name)!;
  }

  registerListeners(): void {
    super.registerListeners();

    this.listenTo(this._layout.DNA, 'change:complement', this.update.bind(this));
    this.listenTo(this.slice, 'change:cutData', this.debouncedUpdate);
    this.listenTo(this.model.get('sequence').get('cutsites'), 'change:color', this.debouncedUpdate);
    this.listenTo(this.model.get('sequence').get('_sequence'), 'change:type', this.showHandler.bind(this));
    this.listenTo(this.overhangsOption(), 'change:visible', function (this: CutSitesView) {
      const ref = this;
      return d3
        .select(this.el)
        .selectAll<SVGElement, CutSite>('.cutsite')
        .each(function (d: CutSite): void {
          ref.renderHover(this, d);
        });
    });
  }

  boxHeight(): number {
    if (this.shouldShow()) {
      return this.layout.boxHeight({overlap: this.max});
    } else {
      return 0;
    }
  }

  maxOverlap(start: number, end: number, data: Array<CutSite>): number {
    const {slice} = this;
    const keys = _.chain(data)
      .filter((site) => {
        const middle = site.start();
        return start <= middle && middle < end;
      })
      .map((site) => [
        [site.sliceData(slice.cid, 'start'), 1],
        [site.sliceData(slice.cid, 'end'), -1],
      ])
      .flatten(true)
      .sortBy((site) => -1 * site[1])
      .sortBy((site) => site[0])
      .value();

    let current = 0;
    let max = 0;

    for (const key of keys) {
      current += key[1];
      max = Math.max(current, max);
    }
    return max;
  }

  shouldShowCutLine(d: CutSite): boolean {
    return this._layout.DNA.get('visible') && (this.overhangsOption().get('visible') || d.get('hover'));
  }

  shouldShowRecognitionSite(d: CutSite): boolean {
    const layoutDna = this._layout.DNA;
    const start = this.slice.start();
    const end = this.slice.end();
    const matchStart = d.sliceData(this.slice.cid, 'matchStart');
    const matchEnd = d.sliceData(this.slice.cid, 'matchEnd');

    const inRange = (start <= matchStart && matchStart < end) || (start <= matchEnd && matchEnd < end);
    const isVisible = layoutDna.get('visible') && d.get('hover');

    return inRange && isVisible;
  }

  handleHoverSelect(d: CutSite, cutsiteD3: d3.Selection<SVGElement, unknown, d3.BaseType, unknown>): void {
    cutsiteD3.classed('selected-cut', d.get('hover') || d.get('selected'));
    this.renderHover(cutsiteD3.node()!, d);
  }

  setLayers(cutsites: Array<CutSite>): void {
    const {slice} = this;
    const used: Array<number | CutSite> = __range__(0, this.max, false).map(() => -1);

    const y = d3
      .scaleLinear()
      .domain([0, this.max])
      .range([this.layout.height({overlap: 0}), this.layout.height({overlap: this.max})]);

    _.chain(cutsites)
      .map((site) => [
        [site.sliceData(slice.cid, 'start'), 1, site],
        [site.sliceData(slice.cid, 'end'), -1, site],
      ])
      .flatten(true)
      .sortBy((site: [number, number, CutSite]) => -1 * site[1])
      .sortBy((site: [number, number, CutSite]) => site[0])
      .each((site: [number, number, CutSite]): void => {
        const siteStart = site[2].start();
        if (slice.start() <= siteStart && siteStart < slice.end()) {
          let index;
          if (site[1] === 1) {
            index = used.lastIndexOf(-1);
            site[2].sliceData(slice.cid, 'layer', y(index));
            used[index] = site[2];
          } else {
            index = used.lastIndexOf(site[2]);
            used[index] = -1;
          }
        } else {
          site[2].sliceData(slice.cid, 'layer', y(0));
        }
      });
  }

  // Augment with rendering values
  augmentCut(cut: CutSite): void {
    const {slice} = this;
    const {layout} = this;
    const {_layout} = this;
    const sequence = this.model.get('sequence');
    const start = slice.start();
    const end = slice.end();

    const label = cut.name();
    const labelWidth = layout
      .get('cutSite')
      .get('type')
      .width(label.length + 1);
    let length = _layout.DNA.get('type').numChars(labelWidth);
    cut.sliceData(slice.cid, 'start', Math.min(cut.start(), end - length));
    cut.sliceData(slice.cid, 'end', Math.min(cut.start() + length, end));
    const position = cut.get('position');
    const index = cut.get('enzyme').get('cut');
    const recognition = cut.get('enzyme').get('seq');

    let matchEnd;
    let matchStart;
    if (cut.strand() >= 0) {
      matchStart = position - index;
      matchEnd = matchStart + recognition.length;
    } else {
      matchEnd = position + cut.get('enzyme').get('offset') + index;
      matchStart = matchEnd - recognition.length;
    }

    length = sequence.length();
    if (matchEnd > length && matchStart > end) {
      matchStart = 0;
      matchEnd %= length;
    }

    if (matchStart < 0 && matchEnd < start) {
      matchStart += length;
      matchEnd = length;
    }

    cut.sliceData(slice.cid, 'matchStart', matchStart);
    cut.sliceData(slice.cid, 'matchEnd', matchEnd);
  }

  renderHover(cutsite: SVGElement, d: CutSite): void {
    if (this.mapType === 'minimap') {
      // DNA is always off for minimap so we can skip this to improve rendering perf
      return;
    }
    const layoutDna = this._layout.DNA;
    const start = this.slice.start();
    const end = this.slice.end();
    const dnaType = layoutDna.get('type');
    const fontType = this.layout.get('cutSite').get('type');
    const {max} = this;
    const x = this.slice.x();

    // RENDER HOVER LINES
    const topY = d.sliceData(this.slice.cid, 'layer') + fontType.height() + fontType.verticalOffset;
    const topDnaY = this.layout.boxHeight({overlap: max}) + this.parent.boxHeightToDNA(this.className);
    const dnaHeight = dnaType.verticalOffset + 2 * layoutDna.height() + layoutDna.get('spacer');
    const middleY = topDnaY + dnaHeight / 2;
    const endY = middleY + dnaHeight / 2;

    const hoverLineData = (
      cStart: number,
      cEnd: number,
      showTop: boolean
    ): Array<Array<[number, number]>> => {
      if (this.shouldShowCutLine(d)) {
        const hoverPts: Array<[number, number]> = [];
        // Line 1
        if (start <= cStart && cStart < end) {
          if (showTop) {
            hoverPts.push([x(cStart) - x(d.sliceData(this.slice.cid, 'start')), topY]);
          }
          hoverPts.push([x(cStart) - x(d.sliceData(this.slice.cid, 'start')), topDnaY]);
          hoverPts.push([x(cStart) - x(d.sliceData(this.slice.cid, 'start')), middleY]);
        }

        // Line 2
        if (layoutDna.get('complement')) {
          if (hoverPts.length === 0) {
            hoverPts.push([x(cStart) - x(d.sliceData(this.slice.cid, 'start')), middleY]);
          }
          hoverPts.push([x(cEnd) - x(d.sliceData(this.slice.cid, 'start')), middleY]);

          // Line 3
          if ((start < cEnd && cEnd <= end) || (start === cEnd && d.get('enzyme').get('offset') < 0)) {
            hoverPts.push([x(cEnd) - x(d.sliceData(this.slice.cid, 'start')), endY]);
          }
        }
        return [hoverPts];
      } else {
        return [];
      }
    };

    const sequence = this.model.get('sequence');
    const data = hoverLineData(d.start(), d.end(), true);
    const enzyme = d.get('enzyme');
    if (enzyme.doubleCutter()) {
      const start2 = d.doubleCutterStart()!;
      const end2 = start2 + enzyme.get('offset2');
      if (start2 < sequence.length() && end2 < sequence.length()) {
        data.push(...hoverLineData(start2, end2, false));
      }
    }

    const lineFn = d3
      .line()
      .x((d) => d[0])
      .y((d) => d[1])
      .curve(d3.curveLinear);
    const hoverLine = d3.select(cutsite).selectAll('.hover-cut-line').data(data);
    hoverLine
      .join((enter) =>
        enter.insert('svg:path', ':first-child').classed('hover-cut-line', true).attr('fill', 'none')
      )
      .attr('d', (d) => lineFn(d));

    const recognitionSiteData = this.shouldShowRecognitionSite(d)
      ? [
          {
            x: x(d.sliceData(this.slice.cid, 'matchStart')) - x(d.sliceData(this.slice.cid, 'start')),
            y: topDnaY,
            height: layoutDna.get('complement') ? endY - topDnaY : endY - middleY,
            width:
              x(d.sliceData(this.slice.cid, 'matchEnd')) -
              x(Math.max(start, d.sliceData(this.slice.cid, 'matchStart'))),
          },
        ]
      : [];

    const recognitionSite = d3.select(cutsite).selectAll('.recognition-site').data(recognitionSiteData);
    recognitionSite
      .join((enter) => enter.insert('svg:rect', ':first-child').classed('recognition-site', true))
      .attr('x', (d) => d.x)
      .attr('y', (d) => d.y)
      .attr('width', (d) => d.width)
      .attr('height', (d) => d.height);
  }

  renderCutsite(cutsite: SVGElement, d: CutSite): void {
    const layoutDna = this._layout.DNA;
    const {layout, _layout, slice, max} = this;
    const sequence = this.model.get('sequence');
    const start = this.slice.start();
    const end = this.slice.end();
    const x = this.slice.x();
    const fontType = layout.get('cutSite').get('type');
    const yPos = d.sliceData(this.slice.cid, 'layer') + this.y(1);
    const xPos = (function () {
      let labelOffset = fontType._width / -2;
      if (d.end() - 1 === d.get('position')) {
        labelOffset *= -1;
      }
      if (x(d.sliceData(slice.cid, 'start')) > Math.abs(labelOffset)) {
        return labelOffset;
      } else {
        return 0;
      }
    })();

    // COLOUR RECTANGLE
    const cutSiteStart = d.start();
    const siteWithinSlice = start <= cutSiteStart && cutSiteStart < end;
    const colorData = siteWithinSlice && d.color() != null ? [d.color()] : [];

    d3.select(cutsite)
      .selectAll('.cutsite-rect')
      .data(colorData)
      .join((enter) => enter.insert('svg:rect', 'text').classed('cutsite-rect', true))
      .attr('x', xPos - 2)
      .attr('y', yPos - fontType.height() + 1)
      .attr('height', fontType.height() + 4)
      .attr('width', d.name().length * fontType.charWidth() + 2)
      .attr('fill', d.color()!);

    // RENDER TEXT
    const textData = siteWithinSlice ? [d.name()] : [];

    d3.select(cutsite)
      .selectAll('text')
      .data(textData)
      .join('svg:text')
      .text(d.name())
      .attr('font-size', fontType.fontSize)
      .attr('x', xPos)
      .attr('y', yPos)
      .attr('fill', d.get('fontColor')!)
      .attr('data-test-component', 'CutSite')
      .attr('data-test-element', 'text')
      .on(
        'mousedown.cutsite',
        // eslint-disable-next-line no-restricted-syntax
        _.bind(function (this: CutSitesView, e: MouseEvent): void {
          this.pieceClickHandler(e, d);
          e.stopPropagation();
        }, this)
      )
      .on(
        'dblclick.cutsite',
        // eslint-disable-next-line no-restricted-syntax
        _.bind(function (this: CutSitesView, e: MouseEvent): void {
          this.pieceDblClickHandler(e, d);
          e.stopPropagation();
        }, this)
      )
      .on('mouseenter.cutsite', () => d.set({hover: true}))
      .on('mouseleave.cutsite', () => d.set({hover: false}))
      .on('contextmenu.cutsite', (e) => {
        if (selectioncontextmenu.open(e, sequence, selectioncontextmenu.CUTSITE_MENU, {cutsite: d})) {
          const selection = sequence.get('selection');
          selection.reset(d.start());
        }
      });

    // MAINLINE

    const mainLineData = siteWithinSlice ? [d] : [];

    const x1 = x(d.start()) - x(d.sliceData(this.slice.cid, 'start'));
    const x2 = x(d.start()) - x(d.sliceData(this.slice.cid, 'start'));
    const y1 = d.sliceData(this.slice.cid, 'layer') + fontType.height() + fontType.verticalOffset;
    let y2 = layout.boxHeight({overlap: max}) + this.parent.boxHeightToDNA(this.className);
    if (layoutDna.get('visible')) {
      y2 += layoutDna.vMargin() + fontType.verticalOffset;
    } else if (this.mapType === 'minimap') {
      y2 += _layout.Axis.get('vMargin');
    }

    d3.select(cutsite)
      .selectAll('.mainLine')
      .data(mainLineData)
      .join((enter) => enter.insert('svg:line', ':first-child').classed('mainLine', true))
      .attr('x1', x1)
      .attr('x2', x2)
      .attr('y1', y1)
      .attr('y2', y2);

    this.renderHover(cutsite, d);
  }

  clickAction(e: MouseEvent, d: CutSite): void {
    d.click(this.model.get('sequence'), e.shiftKey);
  }

  dblClickAction(e: MouseEvent, d: CutSite): void {
    d.dblClick(this.model.get('sequence'), e.shiftKey);
  }

  updateForAdjust(): void {
    this.update(false);
  }

  update(trigger?: boolean): void {
    if (trigger == null) {
      trigger = true;
    }
    if (!this.shouldShow()) {
      return;
    }

    const {slice} = this;
    const start = this.slice.start();
    const end = this.slice.end();
    const x = this.slice.x();
    const ref = this;

    const cutsites = this.slice.get('cutData');
    for (const cut of cutsites) {
      this.augmentCut(cut);
    }

    this.max = this.maxOverlap(start, end, cutsites);
    this.y = d3
      .scaleLinear()
      .domain([0, this.max])
      .range([this.layout.height({overlap: 0}), this.layout.height({overlap: this.max})]);

    this.setLayers(cutsites);

    // Remove old listeners
    this.stopListening(null, 'change:hover');
    this.stopListening(null, 'change:selection');

    d3.select(this.el)
      .selectAll<SVGElement, CutSite>('.cutsite')
      .data(cutsites)
      .join<SVGElement, CutSite>('svg:g')
      .classed('cutsite', true)
      .attr('transform', (d) => `translate(${x(d.sliceData(slice.cid, 'start'))},0)`)
      .attr('class', 'cutsite') // Clear out any old classnames
      .each(function (d: CutSite): void {
        // Note: This is a hot code path so we need to be careful about performance here.
        const cutsiteD3 = d3.select(this);
        ref.handleHoverSelect(d, cutsiteD3);
        ref.listenTo(d, 'change:hover', () => {
          ref.handleHoverSelect(d, cutsiteD3);
        });
        ref.listenTo(d, 'change:selected', () => {
          ref.handleHoverSelect(d, cutsiteD3);
        });
        ref.renderCutsite(this, d);
      });

    if (trigger) {
      return this.trigger('redraw');
    }
  }
}
CutSitesView.initClass();

export default CutSitesView;

function __range__(left: number, right: number, inclusive: boolean): Array<number> {
  const range = [];
  const ascending = left < right;
  const end = !inclusive ? right : ascending ? right + 1 : right - 1;
  for (let i = left; ascending ? i < end : i > end; ascending ? i++ : i--) {
    range.push(i);
  }
  return range;
}
